import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useWeb3React } from '@web3-react/core'
import useAuth from 'hooks/useAuth'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getProfileImageForAddress } from 'lib/avatar'
import useCuratorProfile from 'hooks/useCuratorProfile'
import { useUserRoles } from 'state/user/hooks'
import { cn } from 'lib/utils'

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const { account } = useWeb3React()
  const { logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, loading: profileLoading } = useCuratorProfile()
  const isCurator = useUserRoles().includes('CURATOR')

  const isDashboardActive =
    location.pathname === '/deals' || location.pathname === '/deals/'
  const isPartnersActive = location.pathname.startsWith('/partners')

  const formatWalletAddress = (address: string) =>
    `${address.slice(0, 6)}...${address.slice(-4)}`

  const profileImageSrc =
    profile?.metadata?.logo ||
    (!profileLoading ? getProfileImageForAddress(account) : undefined)

  const handleSignOut = () => {
    try {
      logout()
      navigate('/curators/login')
    } catch {}
  }

  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [])

  return (
    <header className="bg-neutral-white px-4 md:px-8 border-b border-neutral-200 sticky top-0 z-50 h-[64px] font-sans antialiased text-neutral-900">
      <div className="mx-auto flex items-center justify-between h-full max-w-6xl">
        <div className="flex items-center h-full gap-16">
          <Link to="/deals" className="flex items-center gap-2">
            <img
              src="/images/curated-curator-portal.svg"
              alt="Impossible Finance Logo"
              className="h-[36px] w-auto hidden md:block"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-6 h-full">
            <Link
              to="/deals"
              className={cn(
                'text-base font-semibold h-full flex items-center px-1 border-b-2 transition-colors duration-200 font-sans antialiased',
                isDashboardActive
                  ? 'text-brand-primary border-brand-primary'
                  : 'text-neutral-600 border-transparent hover:text-brand-primary hover:border-brand-primary',
              )}
            >
              Dashboard
            </Link>
            {!isCurator && (
              <Link
                to="/partners"
                className={cn(
                  'text-base font-semibold h-full flex items-center px-1 border-b-2 transition-colors duration-200 font-sans antialiased',
                  isPartnersActive
                    ? 'text-brand-primary border-brand-primary'
                    : 'text-neutral-600 border-transparent hover:text-brand-primary hover:border-brand-primary',
                )}
              >
                Partners
              </Link>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-4" ref={menuRef}>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setIsMenuOpen((open) => !open)
              }}
              className="relative inline-flex h-10 items-center justify-center rounded-full pl-4 pr-3 text-sm font-semibold bg-neutral-100 text-neutral-800 transition-colors duration-200"
            >
              <span className="inline-flex items-center gap-2">
                <span className="h-6 w-6 rounded-full overflow-hidden bg-neutral-200 flex items-center justify-center">
                  {profileImageSrc ? (
                    <img
                      src={profileImageSrc}
                      alt="Profile avatar"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="h-6 w-6 rounded-full bg-neutral-200" />
                  )}
                </span>
                <span className="truncate max-w-[140px]">
                  {account ? formatWalletAddress(account) : '—'}
                </span>
                <ChevronDown className="w-4 h-4 text-current" />
              </span>
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg border border-neutral-200 bg-white shadow-lg py-1">
                <Link
                  to="/profile"
                  className="flex items-center px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Profile
                </Link>
                <button
                  type="button"
                  className="flex w-full items-center px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-50"
                  onClick={() => {
                    setIsMenuOpen(false)
                    handleSignOut()
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Navbar
